import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClipboardPaste } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'

export interface TagPasteDialogProps {
  /** Reads the pasted text; the dialog closes once it has been handed over. */
  onSubmit: (text: string) => void
  disabled?: boolean
}

/**
 * A button that opens a box to paste ad tags into — an ad server's tag export,
 * rows copied from a trafficking sheet, or the tags themselves.
 */
export function TagPasteDialog({ onSubmit, disabled }: TagPasteDialogProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')

  const submit = () => {
    onSubmit(text)
    setOpen(false)
    setText('')
  }

  return (
    <>
      <Button type="button" variant="outline" disabled={disabled} onClick={() => setOpen(true)}>
        <ClipboardPaste className="size-4" />
        {t('creativeUpload.pasteTags')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('creativeUpload.pasteTagsTitle')}</DialogTitle>
            <DialogDescription>{t('creativeUpload.pasteTagsHelp')}</DialogDescription>
          </DialogHeader>
          <Textarea
            aria-label={t('creativeUpload.pasteTagsTitle')}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('creativeUpload.pasteTagsPlaceholder')}
            className="max-h-96 min-h-48 font-mono text-xs md:text-xs"
            spellCheck={false}
            autoFocus
          />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" disabled={!text.trim()} onClick={submit}>
              {t('creativeUpload.readTags')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
